import { fetch } from '@utils/utils';
import * as T from '@modules/wantedPersons/_wantedPersonsTypes';
import { Person } from '@components/person/_personTypes';

// structure of entity after it is created with empty data
export const defaultWantedPerson = {};

export const getWantedPerson = ({ addError }) => async (id: string): Promise<T.WantedPerson> => {
  try {
    const response: any = await fetch(`/metropolis/api/wp/people/${id}`, {
      method: 'GET',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    const json: T.WantedPerson = await response.json();

    return json;
  } catch (err) {
    addError(err.message);
    return defaultWantedPerson;
  }
};

export const localLookupPerson = async (person: any): Promise<Array<Person>> => {
  try {
    const response: any = await fetch('/metropolis/api/wp/people/local-lookup', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(person)
    });

    const json = await response.json();

    return json;
  } catch (err) {
    return [];
  }
};

export const externalLookupPerson = async ({ firstName, lastName = '', sex }): Promise<Array<Person>> => {
  const body = {
    name: firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName,
    sex
  };
  try {
    const response: any = await fetch('/metropolis/api/wp/people/external-lookup', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    });

    const json = await response.json();

    return json;
  } catch (err) {
    return [];
  }
};

export const iszrLookupPerson = async (data: any): Promise<any> => {
  const { person, includePhoto } = data;
  try {
    const url = `/metropolis/api/common/iszr/find-person${includePhoto ? '?includePhoto=true' : ''}`;
    const response: any = await fetch(url, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(person)
    });

    if (response.ok) {
      try {
        const json = await response.json();
        return json;
      } catch {
        return null;
      }
    }
    return null;
  } catch (err) {
    return null;
  }
};

export const externalLookupIszr = ({ addSuccess, addError }): any => async (person: any): Promise<any> => {
  try {
    const response: any = await fetch('/metropolis/api/wp/people/iszr-lookup', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(person)
    });

    if (response.ok) {
      try {
        const json = await response.json();
        addSuccess('Údaje předvyplneni z PČR');
        return json;
      } catch {
        addError('Nebyl nalezen');
        return null;
      }
    }
    addError('Nastala chyba systému, prosím kontaktujte administrátora.');
    return null;
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const externalLookupIdentification = async (body): Promise<any> => {
  try {
    const response: any = await fetch('/metropolis/api/wi/identifications/external-lookup', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    });
    if (response.ok) {
      const json = await response.json();
      return json;
    }
    return null;
  } catch (err) {
    return null;
  }
};

export const iszrFindPhoto = ({ addSuccess, addError }): any => async (person: any): Promise<any> => {
  try {
    const response: any = await fetch('/metropolis/api/common/iszr/find-photo', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(person)
    });

    if (response.ok) {
      try {
        const json = await response.json();
        addSuccess('Fotografie stažena z ISZR');
        return json;
      } catch {
        addError('Nebyl nalezen');
        return null;
      }
    }
    addError('Nastala chyba systému, prosím kontaktujte administrátora.');
    return null;
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const crrLookupPersonalData = ({ addSuccess, addError }): any => async (person: any): Promise<any> => {
  try {
    const response: any = await fetch('/metropolis/api/common/iscrr/find-person', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(person)
    });

    if (response.ok) {
      try {
        const json = await response.json();
        addSuccess('Lustrace řidiče úspěšná');
        return json;
      } catch {
        addError('Nebyl nalezen');
        return null;
      }
    }
    addError('Nastala chyba systému, prosím kontaktujte administrátora.');
    return null;
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const crrLookupDrivingLicence = ({ addSuccess, addError }): any => async (
  licenceNumber: string
): Promise<any> => {
  try {
    const response: any = await fetch(
      `/metropolis/api/common/iscrr/find-person/driving-licence?number=${licenceNumber}`,
      {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json' })
      }
    );

    if (response.ok) {
      try {
        const json = await response.json();
        addSuccess('Lustrace řidiče úspěšná');
        return json;
      } catch {
        addError('Nebyl nalezen');
        return null;
      }
    }
    addError('Nastala chyba systému, prosím kontaktujte administrátora.');
    return null;
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const aiseoLookup = async (data: any): Promise<any> => {
  try {
    const response: any = await fetch('/metropolis/api/common/aiseo/find-person', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data)
    });

    if (response.ok) {
      try {
        const json = await response.json();
        return json;
      } catch {
        return null;
      }
    }
    return null;
  } catch (err) {
    return null;
  }
};

export const aiseoAifoLookup = async (aifo: string): Promise<any> => {
  try {
    const response: any = await fetch('/metropolis/api/common/aiseo/find-person-aifo', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ aifo })
    });

    if (response.ok) {
      try {
        const json = await response.json();
        return json;
      } catch {
        return null;
      }
    }
    return null;
  } catch (err) {
    return null;
  }
};

export const aiscLookup = async (data: any): Promise<any> => {
  try {
    const response: any = await fetch('/metropolis/api/common/aisc/find-person', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data)
    });

    if (response.ok) {
      try {
        const json = await response.json();
        return json;
      } catch {
        return null;
      }
    }
    return null;
  } catch (err) {
    return null;
  }
};



// WEBPACK FOOTER //
// ./src/modules/wantedPersons/_wantedPersonsActions.ts